import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import { buildUrl, formatAmount, truncateKeywordSpaces, toQueryString } from 'utils';
import { Grid, Paper, Container, InputAdornment, Button } from '@material-ui/core';
import Layout from 'components/Layout';
import Title from 'components/Title';
import { AccountStatementActions } from '../../state/modules/accountStatement';
import { Table } from 'components/UI';
import { useStyles } from './AccountStatementList.styled';
import { statusStyles } from './Common.styled';
import { FETCH_STATE } from 'constants';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'hooks';
import { Search as SearchIcon } from '@material-ui/icons';
import { SearchField } from 'components/FormFields';
import { debounce } from 'lodash';
import { MonthSelect, YearSelect, AccountStatementStatusSelect } from '../../components/DataFields';
import { accessControl } from 'components/AccessControl';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

const AccountStatementList = () => {
  const urlParams = useQuery();
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const statusClasses = statusStyles();
  const isMounted = useRef(false);
  const abortController = useRef(null);
  const [selectedAccountStatements, setSelectedAccountStatements] = useState([]);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);

  const data = useSelector((state) => state.accountStatement.list);
  const pagination = useSelector((state) => state.accountStatement.pagination);
  const fetchingAccountStatement = useSelector((state) => state.accountStatement.fetchingList);
  const processingAccountStatement = useSelector((state) => {
    return (
      state.accountStatement.processingBalance === 'LOADING' ||
      state.accountStatement.processingBalanceBulk === 'LOADING'
    );
  });
  const processingBalance = useSelector((state) => state.accountStatement.processingBalance);
  const processingBalanceBulk = useSelector(
    (state) => state.accountStatement.processingBalanceBulk
  );
  const currentOrganization = useSelector((state) => state.auth?.currentOrganization);
  const locale = useSelector(
    (state) => state?.organizations?.organization?.locale || 'en-US'
  ).replace('_', '-');

  const isOwner = currentOrganization?.organization_type === 'OWNER';

  // Who may process a balance on demand. `enabled` matters: both endpoints sit behind
  // @enabled('account_statement'), so without it every click returns NOTENABLED.
  // accessControl uses useSelector, so these calls must stay unconditional.
  const canProcessBalance = accessControl([
    'configuration.account_statement.enabled.true',
    'AND',
    'privilege.account_statement.process',
  ]);
  const payerOnManualBilling = accessControl([
    'configuration.account_statement.automatic_payments.false',
  ]);

  // Gates the payer only, so this can ship dark. Not the fleet owner: they have the
  // action in production today, and the flag holds back what is new, not what works.
  const manualPaymentEnabled = useFeatureIsOn('feat.billing.manual-payment-processing');

  const refetch = fetchingAccountStatement === FETCH_STATE.UNSENT;
  const loading = fetchingAccountStatement === FETCH_STATE.LOADING;

  const fetchAccountStatement = useCallback(
    (params = {}) => {
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        AccountStatementActions.fetchAccountStatements(params, {
          signal: abortController?.current?.signal,
        })
      );
    },
    [dispatch]
  );

  const fetchAccountStatementDebounced = useCallback(
    debounce((params = {}) => {
      fetchAccountStatement(params);
    }, 1000),
    [dispatch]
  );

  // Fetch on params change
  useEffect(() => {
    if (isMounted.current) {
      fetchAccountStatementDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchAccountStatement, params]);

  // Fetch when data is outdated
  useEffect(() => {
    if (refetch) {
      fetchAccountStatement(params);
    }
  }, [fetchAccountStatement, params, refetch]);

  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    const qs = toQueryString(newParams);
    history.replace(buildUrl(`/account-statement${qs}`));
    setParams(newParams);
  };

  const handleSearch = (name, value) => {
    const searchParams = {
      ...params,
      page: 1,
      [name]: truncateKeywordSpaces(value),
    };
    if (value === '') {
      delete searchParams[name];
    }
    const qs = toQueryString(searchParams);
    history.replace(buildUrl(`/account-statement${qs}`));
    setParams(searchParams);
  };

  const handlePageChange = (page) => {
    const newParams = { ...params, page };
    const qs = toQueryString(newParams);
    history.replace(buildUrl(`/account-statement${qs}`));
    setParams(newParams);
  };

  const handlePerRowsChange = async (limit) => {
    const newParams = { ...params, page: 1, limit };
    const qs = toQueryString(newParams);
    history.replace(buildUrl(`/account-statement${qs}`));
    setParams(newParams);
  };

  const handleSelectedRowsChange = (row) => {
    setSelectedAccountStatements(row.selectedRows);
  };

  // Keyed on DONE rather than on the dispatch returning: the thunks resolve either way,
  // so awaiting them would also clear the selection on a rejected request.
  // `clearSelectedRows` is a toggle — flipping it is what makes the table drop its boxes.
  useEffect(() => {
    if (processingBalance === FETCH_STATE.DONE || processingBalanceBulk === FETCH_STATE.DONE) {
      setSelectedAccountStatements([]);
      setClearSelectedRows((previous) => !previous);
    }
  }, [processingBalance, processingBalanceBulk]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchAccountStatement({ ...params });
  }, []);

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/account-statement/${row?.id}`));
  };

  const columns = [
    {
      name: i18n.t('common.id'),
      selector: 'id',
      grow: 0,
      minWidth: '6rem',
      sortable: true,
    },
    ...(isOwner
      ? [
          {
            name: i18n.t('common.partner'),
            selector: 'partner',
            format: (row) => row?.payer?.customer_name,
            grow: 2,
            sortable: false,
          },
        ]
      : []),
    {
      name: i18n.t('common.entries'),
      selector: 'total_invoices',
      format: (row) => row?.total_invoices,
      grow: 1,
      sortable: false,
    },
    {
      name: i18n.t('common.total'),
      selector: 'amount',
      format: (row) => `$${formatAmount(row.total || 0)}`,
      grow: 1,
      sortable: false,
    },
    {
      name: i18n.t('common.month'),
      selector: 'total_emitted',
      format: (row) =>
        new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(row.due_time)),
      grow: 1,
      sortable: false,
    },
    {
      name: i18n.t('common.year'),
      selector: 'start_time',
      format: (row) =>
        new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(new Date(row.due_time)),
      grow: 1,
      sortable: false,
    },
    {
      name: i18n.t('common.status'),
      selector: 'expiration_time',
      format: (row) => (
        <span className={statusClasses[row.status]}>
          {i18n.t(`account_statement.statuses.${row.status}`, { defaultValue: row.status })}
        </span>
      ),
      grow: 1,
      sortable: false,
    },
    {
      name: i18n.t('common.partnership'),
      selector: 'expiration_time',
      format: (row) => (
        <span className={statusClasses[row?.relationship?.status]}>
          {i18n.t(`partners.statuses.${row?.relationship?.status}`, {
            defaultValue: row?.relationship?.status,
          })}
        </span>
      ),
      grow: 1,
      sortable: false,
    },
  ];

  const onProcessBalance = () => {
    if (selectedAccountStatements.length == 1) {
      const row = selectedAccountStatements[0];
      dispatch(AccountStatementActions.processBalanceAccountStatementById(row.id));
    } else {
      dispatch(
        AccountStatementActions.processBalanceAccountStatementBulk({
          account_statement_ids: selectedAccountStatements.map(
            (accountStatement) => accountStatement.id
          ),
        })
      );
    }
  };

  const showProcessBalanceButton =
    canProcessBalance && (isOwner || (payerOnManualBilling && manualPaymentEnabled));
  const hasSelectableRows = showProcessBalanceButton;

  return (
    <Layout title={i18n.t('account_statement.title')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid item>
                <Title title={i18n.t('account_statement.title')} />
              </Grid>
              {showProcessBalanceButton ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onProcessBalance}
                  disabled={
                    loading || selectedAccountStatements.length < 1 || processingAccountStatement
                  }
                  style={{ textTransform: 'capitalize' }}
                >
                  {i18n.t('invoice.process_balance')}
                </Button>
              ) : null}
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="flex-end">
              {isOwner ? (
                <Grid item xs={3}>
                  <SearchField
                    margin="none"
                    label={i18n.t('account_statement.search')}
                    field={{
                      name: 'keyword',
                      onChange: handleSearch,
                      value: params.keyword,
                    }}
                    inputProps={{
                      startAdornment: (
                        <InputAdornment
                          classes={{
                            root: classes.searchIcon,
                          }}
                          position="start"
                        >
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              ) : null}
              <Grid item xs={3}>
                <MonthSelect
                  field={{ name: 'month', value: params.month }}
                  form={{ setFieldValue: handleSearch }}
                  label={i18n.t('common.by_month')}
                />
              </Grid>
              <Grid item xs={3}>
                <YearSelect
                  field={{ name: 'year', value: params.year }}
                  form={{ setFieldValue: handleSearch }}
                  label={i18n.t('common.by_year')}
                />
              </Grid>
              <Grid item xs={3}>
                <AccountStatementStatusSelect
                  form={{ setFieldValue: handleSearch }}
                  field={{ name: 'status', value: params.status }}
                  label={i18n.t('common.by_status')}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data={data}
                columns={columns}
                onSort={handleSort}
                loading={loading}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                handleSelectedRowsChange={handleSelectedRowsChange}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
                page={params?.page}
                limit={params?.limit}
                defaultSortField={params?.sort_field}
                defaultSortAsc={!params?.sort_desc}
                selectableRows={hasSelectableRows}
                clearSelectedRows={clearSelectedRows}
              />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export { AccountStatementList };
